"use client";

import { useState, useEffect } from "react";
import {
	X,
	ChevronLeft,
	ChevronRight,
	ZoomIn,
	ZoomOut,
	Download,
} from "lucide-react";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import modalStyles from "./ViewPhotosModal.module.css";
import Image from "next/image";

interface ViewPhotosModalProps {
	isOpen: boolean;
	onClose: () => void;
	address: string;
	photos: string[];
	initialIndex?: number;
}

export default function ViewPhotosModal({
	isOpen,
	onClose,
	address,
	photos,
	initialIndex = 0,
}: ViewPhotosModalProps) {
	const [activePhotoIndex, setActivePhotoIndex] = useState(initialIndex);
	const [zoom, setZoom] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

	const handleNextPhoto = () => {
		setActivePhotoIndex((prev) => (prev + 1) % photos.length);
		setZoom(1);
		setPosition({ x: 0, y: 0 });
	};

	const handlePrevPhoto = () => {
		setActivePhotoIndex(
			(prev) => (prev - 1 + photos.length) % photos.length
		);
		setZoom(1);
		setPosition({ x: 0, y: 0 });
	};

	const handleZoomIn = () => {
		setZoom((prev) => Math.min(prev + 0.5, 3));
	};

	const handleZoomOut = () => {
		if (zoom > 1) {
			const newZoom = Math.max(zoom - 0.5, 1);
			setZoom(newZoom);
			if (newZoom === 1) {
				setPosition({ x: 0, y: 0 });
			}
		}
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		if (zoom > 1) {
			setIsDragging(true);
			setDragStart({
				x: e.clientX - position.x,
				y: e.clientY - position.y,
			});
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging && zoom > 1) {
			setPosition({
				x: e.clientX - dragStart.x,
				y: e.clientY - dragStart.y,
			});
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	useEffect(() => {
		if (isOpen) {
			setActivePhotoIndex(initialIndex);
			setZoom(1);
			setPosition({ x: 0, y: 0 });
		}
	}, [isOpen, initialIndex]);

	const handleDownload = () => {
		const link = document.createElement("a");
		link.href = photos[activePhotoIndex];
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		link.download = `property-photo-${activePhotoIndex + 1}.jpg`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<AlertDialog open={isOpen} onOpenChange={onClose}>
			<AlertDialogContent className={modalStyles.photoModalContent}>
				<div className="px-6 py-4 bg-white border-b border-[#e5e7eb] shrink-0 relative rounded-t-lg flex items-center justify-between">
					<div>
						<AlertDialogTitle className="font-size-[1.25rem] font-bold text-[#12301b]">
							Property Photos
						</AlertDialogTitle>
						<p className="text-sm text-[#8c99a7] mt-1">{address}</p>
					</div>
				</div>

				<div className="flex flex-1 overflow-hidden min-h-0">
					<div className="w-[280px] border-r border-[#e5e7eb] overflow-y-auto bg-[#f8f9fa] shrink-0">
						<div className="p-4 space-y-3">
							{photos.map((photo, index) => (
								<button
									key={index}
									onClick={() => {
										setActivePhotoIndex(index);
										setZoom(1);
									}}
									className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${
										index === activePhotoIndex
											? "border-[#417714] ring-2 ring-[#A7FF5E]/50"
											: "border-transparent hover:border-[#dde3e9]"
									}`}
								>
									<Image
										fill
										src={photo}
										alt={`Photo ${index + 1}`}
										className="w-full h-full object-cover"
									/>
									<div
										className={`absolute bottom-2 right-2 px-2.5 py-1 rounded text-sm font-semibold ${
											index === activePhotoIndex
												? "bg-[#417714] text-white"
												: "bg-black/70 text-white"
										}`}
									>
										{index + 1}
									</div>
								</button>
							))}
						</div>
					</div>

					<div className="flex-1 flex flex-col bg-[#1a1a1a] min-w-0 min-h-0">
						<div
							className="flex-1 relative overflow-hidden flex items-center justify-center p-8"
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseUp}
							style={{
								backgroundColor: "white",
								cursor:
									zoom > 1
										? isDragging
											? "grabbing"
											: "grab"
										: "default",
							}}
						>
							<Image
								fill
								src={photos[activePhotoIndex]}
								alt={`Photo ${activePhotoIndex + 1}`}
								className="object-contain transition-transform duration-200 select-none"
								style={{
									transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
									pointerEvents: "none",
								}}
								draggable={false}
							/>
						</div>
					</div>
				</div>

				<div className="bg-[#0d0d0d] border-t border-[#333333] px-6 py-4 shrink-0 rounded-b-lg">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<button
								onClick={handlePrevPhoto}
								className="size-[36px] flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
							>
								<ChevronLeft className="size-[20px] text-white" />
							</button>
							<span className="text-white font-medium text-sm px-3">
								{activePhotoIndex + 1} / {photos.length}
							</span>
							<button
								onClick={handleNextPhoto}
								className="size-[36px] flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
							>
								<ChevronRight className="size-[20px] text-white" />
							</button>
						</div>

						<div className="flex items-center gap-2">
							<button
								onClick={handleZoomOut}
								disabled={zoom <= 1}
								className="size-[36px] flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<ZoomOut className="size-[18px] text-white" />
							</button>
							<span className="text-white text-sm font-medium min-w-[50px] text-center">
								{Math.round(zoom * 100)}%
							</span>
							<button
								onClick={handleZoomIn}
								disabled={zoom >= 3}
								className="size-[36px] flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<ZoomIn className="size-[18px] text-white" />
							</button>
						</div>

						<button
							onClick={handleDownload}
							className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A7FF5E] hover:bg-[#8de03f] text-[#16251c] font-semibold text-sm transition-colors"
						>
							<Download className="size-4" />
							Download
						</button>
					</div>
				</div>

				<button
					type="button"
					onClick={onClose}
					className="absolute top-4 right-4 z-50 p-2"
				>
					<X className="size-5 text-gray-900" />
					<span className="sr-only">Close</span>
				</button>
			</AlertDialogContent>
		</AlertDialog>
	);
}
